<template>
  <CFormItem :prop="name" :field-value="value">
    <component
      :is="params.withButton ? ElInputNumber : ElInput"
      v-model="value"
      type="number"
      class="cell-number-edit clean-number-input"
      :class="{ 'show-edit-icon': editByIcon }"
      controls-position="right"
      :disabled="disabled || editByIcon"
      :placeholder="placeholder"
      :max="params.elMax ?? max"
      :min="params.elMin ?? min"
      :step="params.elStep"
      :value-on-clear="params.elMin"
      @change="valueChange"
      @focus="autoSelect($event)"
      @keydown.stop="noop"
      @click.stop="noop"
      @blur="editByIcon = params.editByIcon"
    >
      <template v-if="editByIcon && !disabled" #suffix>
        <ElIcon
          class="edit-icon"
          color="#327DE7"
          size="12px"
          @click="editByIcon = false"
        >
          <EditPen />
        </ElIcon>
      </template>
    </component>
  </CFormItem>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { ElInput, ElInputNumber } from 'element-plus';
import { EditPen } from '@element-plus/icons-vue';
import { ICellRendererParams } from 'ag-grid-community';
import { noop, isString, isFunction } from 'lodash-es';
import { useShareData } from '../../core/SharedData';
import { formatDecimal } from '../../core/Common';
import { getNumberLimit, editLimit } from '../../core/InputNumberUtil';

const props = defineProps<{
  params: ICellRendererParams<any> & {
    fieldName?: string; // 字段名
    /*小数位数 */
    decimal?: number;
    /*编辑框是否禁用 */
    disabled?: boolean | string | ((ps: ICellRendererParams<any>) => boolean);

    invalidText?: string; // NaN时显示的字符串

    // 点击自动选中文字
    autoSelect?: boolean;

    thin?: boolean; // 比较窄的模式

    withButton?: boolean; // 右侧按钮(上下箭头加减)

    editByIcon?: boolean; // 点击icon进入编辑模式, 而不是显示编辑框

    // ElInputNumber 属性
    elMax?: number;
    elMin?: number;
    elStep?: number;
    // ElInputNumber有个问题 当手动删除输入的数字后 向后端提交的数字依旧存在 但是前端不显示这个数字了 且不会触发change事件
    // 可以通过该属性控制删除后在前端显示的数字
    // elValueOnClear?: number | null | 'min' | 'max';

    valueChange?: (
      params: ICellRendererParams<any>,
      newValue: any,
      oldValue: string
    ) => void;
  };
}>();

// 字段名
// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
const name = props.params.fieldName || props.params.colDef?.field!;

const { max, min } = getNumberLimit(
  props.params.withButton || props.params.decimal !== undefined
);

const tableSetting = useShareData('table', 'setting');

// 显示edit icon
const editByIcon = ref(props.params.editByIcon);

const disabled = computed(() => {
  // table全局禁止edit
  if (tableSetting?.disabledEdit) {
    return true;
  }

  const paramsDisabled = props.params.disabled;
  return isString(paramsDisabled)
    ? !!props.params.data[paramsDisabled]
    : isFunction(paramsDisabled)
    ? paramsDisabled(props.params)
    : paramsDisabled;
});

// eslint-disable-next-line vue/no-setup-props-destructure
const useNumberInput = props.params.withButton;

// eslint-disable-next-line vue/no-setup-props-destructure
let v = props.params.value;
// 小数位处理
if (props.params.decimal !== undefined && !isNaN(v)) {
  v = formatDecimal((v || '').toString(), props.params.decimal);
}

// 处理无效值
function getPlaceholder(v: any) {
  if (v && isNaN(v)) return props.params.invalidText || '无效数据';
  return '';
}

const value = ref(isNaN(v) ? undefined : useNumberInput ? Number(v) : v);
const placeholder = ref(getPlaceholder(v));

async function valueChange(val: string) {
  try {
    // 小数位处理
    if (props.params.decimal !== undefined) {
      val = editLimit(
        val,
        props.params.elMax ?? max,
        props.params.elMin ?? min
      );
      val = formatDecimal((val || '').toString(), props.params.decimal);
      value.value = useNumberInput ? Number(val) : val;
    }
    await props.params.valueChange?.(props.params, val, v);
    props.params.node.setDataValue(name, val, 'form'); // 'form'参数用于区分谁触发的
  } catch (e) {
    value.value = useNumberInput ? Number(v) : v;
    placeholder.value = '';
    throw e;
  }
}

function autoSelect(event: any) {
  if (props.params.autoSelect) {
    event.target.select();
  }
}

// 窄模式
const margin = props.params.thin ? '0px' : '8px';
const height = props.params.thin ? '22px' : 'var(--el-input-inner-height)';
const padding = props.params.thin ? '6px' : '11px';
</script>

<style lang="scss">
.cell-number-edit {
  width: unset !important;
  margin-left: v-bind(margin);
  margin-right: v-bind(margin);

  &.show-edit-icon,
  &.is-disabled {
    cursor: default !important;

    .el-input__wrapper {
      --el-disabled-bg-color: transparent;
      --el-disabled-text-color: var(--el-input-text-color);

      border-color: transparent !important;
      background: transparent !important;
      box-shadow: none !important;

      input {
        cursor: default;
      }
    }

    .el-input-number__increase,
    .el-input-number__decrease {
      display: none;
    }
  }

  .el-input__wrapper {
    border: 1px solid #dcdfe655;
    box-shadow: none;
    padding: 1px v-bind(padding);
  }

  &.is-controls-right {
    .el-input__wrapper {
      min-height: calc(var(--el-input-height) - 2px);
      border: 1px solid #dcdfe6;
    }
  }

  .el-input__wrapper:hover,
  .el-input__wrapper.is-focus {
    border-color: #dcdfe6;
    box-shadow: none;
  }

  .el-input__inner {
    height: v-bind(height);
  }

  input {
    text-align: right !important;
  }

  .edit-icon {
    cursor: pointer;
  }
}
</style>
